// @flow
import React, {Component} from 'react';
import {FormattedMessage} from 'react-intl';
import {debounce} from 'lodash';
import FormattedCurrencyRange from '../FormattedCurrencyRange/FormattedCurrencyRange';

import './PriceSlider.less';
import Slider from '../Slider/Slider';
import {getMaxDisplayPrice, getMaxPrice} from './utils';
import {triggerSelectUnselectFilterEdapEvent} from '../../utils/filtersEdapUtils';

export type BoundsType = Array<number | null>;
type BoundsTypeInSlider = Array<number>;
type ComponentProps = {
    currency: string,
    hasDates: ?boolean,
    minPrice: ?number,
    maxPrice: ?number,
    onPriceChange: (BoundsType) => any,
    className?: string,
    locale: string
};

export const nullIfMaxBounds = (
    maxAllowedPrice: number,
    callback: (BoundsType) => any
) => {
    return (bounds: Array<number>) => {
        let newBounds: BoundsType = [];
        if (bounds[1] && bounds[1] >= maxAllowedPrice) {
            newBounds = [bounds[0], null];
        } else {
            newBounds = newBounds.concat(bounds);
        }
        callback(newBounds);
    };
};

type Props = ComponentProps;

type State = {
    selectedMinPrice: number,
    selectedMaxPrice: number
};

export default class PriceSlider extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.updateLocalStateWithSelectedPrice = debounce(
            this.updateLocalStateWithSelectedPrice,
            200,
            {leading: false, trailing: true}
        );

        const maxPrice = getMaxDisplayPrice(
            getMaxPrice(props.currency),
            props.maxPrice
        );

        this.state = {
            selectedMinPrice: props.minPrice || 0,
            selectedMaxPrice: maxPrice
        };
    }

    updateLocalStateWithSelectedPrice = (bounds: BoundsTypeInSlider): void => {
        const {selectedMinPrice, selectedMaxPrice} = this.state;

        if (bounds[0] !== selectedMinPrice || bounds[1] !== selectedMaxPrice) {
            const eventlabel = `nightly_price${
                bounds[0] !== selectedMinPrice
                    ? `_min_${bounds[0]}`
                    : `_max_${bounds[1]}`
            }`;
            triggerSelectUnselectFilterEdapEvent({
                eventaction: `filter selected`,
                eventlabel
            });

            this.setState({
                selectedMinPrice: bounds[0],
                selectedMaxPrice: bounds[1]
            });
        }
    };

    render() {
        const props = this.props;
        const {
            currency,
            hasDates,
            onPriceChange,
            className = '',
            locale
        } = props;
        const maxAllowedPrice = getMaxPrice(currency);
        const minPrice: number = props.minPrice || 0;
        const maxDisplayPrice = getMaxDisplayPrice(
            maxAllowedPrice,
            props.maxPrice
        );

        const isMax = maxDisplayPrice >= maxAllowedPrice;
        const isAny: boolean = minPrice === 0 && isMax;

        const onChange = (bounds: BoundsTypeInSlider): void => {
            nullIfMaxBounds(maxAllowedPrice, onPriceChange)(bounds);

            this.updateLocalStateWithSelectedPrice(bounds);
        };

        return (
            <div className={`PriceSlider ${className}`}>
                <div className="PriceSlider__priceLabel">
                    <span className="PriceSlider__priceLabel__values">
                        {isAny ? (
                            <FormattedMessage
                                id={'priceAnyLabel'}
                                defaultMessage={'Any price'}
                            />
                        ) : (
                            <FormattedCurrencyRange
                                minPrice={minPrice}
                                maxPrice={maxDisplayPrice}
                                currency={currency}
                                isMax={isMax}
                                locale={locale}
                            />
                        )}
                    </span>
                    <span className="PriceSlider__priceLabel__perNight">
                        {hasDates ? (
                            <FormattedMessage
                                id={'ratesNightly'}
                                defaultMessage={'per night'}
                            />
                        ) : (
                            <FormattedMessage
                                id={'ratesAverage'}
                                defaultMessage={'avg/night'}
                            />
                        )}
                    </span>
                </div>
                <div
                    className="PriceSlider__slider"
                    data-wdio="PriceSlider__slider"
                >
                    <Slider
                        start={minPrice}
                        stop={maxDisplayPrice}
                        max={maxAllowedPrice}
                        step={10}
                        onChange={onChange}
                    />
                </div>
            </div>
        );
    }
}
